// Wave 46 · Soundbox — 100% browser Web-Speech + Web-Audio API. Zero server cost.
// Global event bus for cross-page audible announcements.
//
// Usage anywhere:
//   import { announce } from '../../utils/soundbox';
//   announce('new_b2c_demand', { title: 'Rice bags', amount: 500 });

const PREF_KEY    = 'soundbox_prefs_v1';
const LAST_KEY    = 'soundbox_last_utterance';
const HISTORY_KEY = 'soundbox_history_v1';
const MUTED_CATS_KEY = 'soundbox_muted_cats_v1'; // per-category mute map

const DEFAULT_PREFS = {
  mute: false,
  rate: 1.0,
  lang: 'hi-IN',
  voice_name: null,
};

export function getPrefs() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch (_) {}
  return { ...DEFAULT_PREFS };
}

export function setPrefs(patch) {
  const cur = getPrefs();
  const next = { ...cur, ...patch };
  try { localStorage.setItem(PREF_KEY, JSON.stringify(next)); } catch (_) {}
  window.dispatchEvent(new CustomEvent('soundbox:prefs', { detail: next }));
  return next;
}

export function listVoices() {
  try {
    const v = window.speechSynthesis && window.speechSynthesis.getVoices();
    return Array.isArray(v) ? v : [];
  } catch (_) { return []; }
}

export function listLanguages() {
  const set = new Set();
  listVoices().forEach((v) => set.add(v.lang));
  const base = ['hi-IN', 'en-IN', 'en-US', 'mr-IN', 'gu-IN', 'ta-IN', 'pa-IN', 'bn-IN'];
  base.forEach((l) => set.add(l));
  return [...set].sort();
}

// ============ Shared AudioContext (unlocked on first user gesture) ============
let _audioCtx = null;
let _soundboxUnlocked = false;

function getAudioContext() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_audioCtx) _audioCtx = new AC();
  return _audioCtx;
}

// Export: resume AudioContext + prime speechSynthesis — call on user gesture
export function unlockSoundbox() {
  _soundboxUnlocked = true;
  try { localStorage.setItem('soundbox_unlocked', '1'); } catch (_) {}
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch (_) {}
  try {
    if (window.speechSynthesis) {
      const u = new SpeechSynthesisUtterance('\u00a0');
      u.volume = 0; u.rate = 2;
      window.speechSynthesis.speak(u);
    }
  } catch (_) {}
}

export function isSoundboxUnlocked() {
  if (_soundboxUnlocked) return true;
  try { return !!localStorage.getItem('soundbox_unlocked'); } catch (_) { return false; }
}

if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  };
  ['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
    window.addEventListener(evt, unlockAudio, { once: true, passive: true })
  );
}

// ============ Chime (Web Audio API, no external asset) ============
function playChime() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const schedule = () => {
      [880, 1108].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const start = ctx.currentTime + i * 0.16;
        g.gain.setValueAtTime(0.001, start);
        g.gain.exponentialRampToValueAtTime(0.16, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, start + 0.45);
        o.start(start); o.stop(start + 0.48);
      });
    };
    if (ctx.state === 'suspended') {
      ctx.resume().then(schedule).catch(() => {});
    } else {
      schedule();
    }
  } catch (_) {}
}

// ============ Speak helper ============
function speak(text, prefsOverride) {
  const p = { ...getPrefs(), ...(prefsOverride || {}) };
  if (p.mute) return;
  try {
    if (!window.speechSynthesis) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = Number(p.rate) || 1.0;
    utter.pitch = 1.0;
    utter.volume = 0.95;
    utter.lang = p.lang || 'hi-IN';
    // Voice matching
    const voices = listVoices();
    let voice = null;
    if (p.voice_name) voice = voices.find((v) => v.name === p.voice_name) || null;
    if (!voice) voice = voices.find((v) => v.lang === utter.lang) || null;
    if (voice) utter.voice = voice;
    // Cancel queue to avoid stacking (announcements should be fresh)
    try { window.speechSynthesis.cancel(); } catch (_) {}
    window.speechSynthesis.speak(utter);
    try { localStorage.setItem(LAST_KEY, text); } catch (_) {}
    if (!(prefsOverride && prefsOverride.__noHistory)) pushHistory(text);
  } catch (_) {}
}

function pushHistory(text) {
  try {
    const list = getHistory();
    list.unshift({ text, at: new Date().toISOString() });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 10)));
    window.dispatchEvent(new CustomEvent('soundbox:history'));
  } catch (_) {}
}

export function getHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch (_) { return []; }
}

export function replayText(text) {
  if (!text) return;
  playChime();
  setTimeout(() => speak(text, { __noHistory: true }), 220);
}

export function repeatLast() {
  try {
    const t = localStorage.getItem(LAST_KEY);
    if (t) { playChime(); speak(t, { __noHistory: true }); }
  } catch (_) {}
}

// ============ Per-category mute ============
export function getMutedCategories() {
  try {
    const stored = localStorage.getItem(MUTED_CATS_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    // Safety: ensure it's a plain object with boolean values
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch (_) { return {}; }
}

export function setMutedCategories(map) {
  try { localStorage.setItem(MUTED_CATS_KEY, JSON.stringify(map)); }
  catch (_) {}
  window.dispatchEvent(new CustomEvent('soundbox:muted-cats', { detail: map }));
  return map;
}

export function toggleCategoryMute(category) {
  const cur = getMutedCategories();
  const next = { ...cur, [category]: !cur[category] };
  return setMutedCategories(next);
}

export function isCategoryMuted(category) {
  return !!getMutedCategories()[category];
}

// ============ Templates (per event × language) ============
const TEMPLATES = {
  new_b2c_demand: {
    'hi-IN': (d) => `Nayi B2C demand aayi. ${d.title || 'Item'}, budget ${d.amount || 0} AT.`,
    'en-IN': (d) => `New retail demand posted: ${d.title || 'item'} with budget ${d.amount || 0} AT.`,
    'en-US': (d) => `New retail demand: ${d.title || 'item'} budget ${d.amount || 0} AT.`,
  },
  new_b2b_demand: {
    'hi-IN': (d) => `Nayi B2B thok demand: ${d.title || 'Item'}, quantity ${d.qty || 0} pieces.`,
    'en-IN': (d) => `New bulk B2B demand: ${d.title || 'item'}, quantity ${d.qty || 0} pieces.`,
    'en-US': (d) => `New bulk demand: ${d.title || 'item'}, quantity ${d.qty || 0}.`,
  },
  new_bid_received: {
    'hi-IN': (d) => `Nayi bid aayi: ${d.amount || 0} AT ${d.kind === 'sale' ? 'aapke sale post' : 'aapki demand'} par.`,
    'en-IN': (d) => `New bid received: ${d.amount || 0} AT on your ${d.kind === 'sale' ? 'sale post' : 'demand'}.`,
    'en-US': (d) => `New bid: ${d.amount || 0} AT.`,
  },
  deal_locked: {
    'hi-IN': (d) => `Deal lock ho gaya. ${d.amount || 0} AT escrow mein.`,
    'en-IN': (d) => `Deal locked. ${d.amount || 0} AT held in escrow.`,
    'en-US': (d) => `Deal locked, ${d.amount || 0} AT in escrow.`,
  },
  escrow_confirmed: {
    'hi-IN': (d) => `Payment released. ${d.amount || 0} AT aapke wallet mein aa gaye.`,
    'en-IN': (d) => `Payment released: ${d.amount || 0} AT credited to your wallet.`,
    'en-US': (d) => `Payment released, ${d.amount || 0} AT credited.`,
  },
  new_order: {
    'hi-IN': (d) => `Naya order aaya. Total ${d.amount || 0} AT.`,
    'en-IN': (d) => `New order received. Total ${d.amount || 0} AT.`,
    'en-US': (d) => `New order, total ${d.amount || 0} AT.`,
  },
  // Support fix 2026-09-24: marketplace order status steps
  order_confirmed: {
    'hi-IN': (d) => `Order confirm ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order confirmed${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order confirmed${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_preparing: {
    'hi-IN': (d) => `Order taiyaar ho raha hai${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order is being prepared${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order is being prepared${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_packed: {
    'hi-IN': (d) => `Order pack ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order packed${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order packed${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_shipped: {
    'hi-IN': (d) => `Order ship ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order shipped${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order shipped${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_out_for_delivery: {
    'hi-IN': (d) => `Order delivery ke liye nikal gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order is out for delivery${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order is out for delivery${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_delivered: {
    'hi-IN': (d) => `Order deliver ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order delivered${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order delivered${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_completed: {
    'hi-IN': (d) => `Order deliver ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order delivered${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order delivered${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_rejected: {
    'hi-IN': (d) => `Order reject ho gaya${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order rejected${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order rejected${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_pending: {
    'hi-IN': (d) => `Naya order place hua${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `New order placed${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `New order placed${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  order_update: {
    'hi-IN': (d) => `Order status update hua${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Order status updated${d.amount ? '. Total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Order status updated${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  payment_received: {
    'hi-IN': (d) => `Payment received. ${d.amount || 0} AT wallet mein aaye.`,
    'en-IN': (d) => `Payment received: ${d.amount || 0} AT credited.`,
    'en-US': (d) => `Payment received: ${d.amount || 0} AT.`,
  },
  // Wave 47 · AT deposit funnel
  at_deposit_request: {
    'hi-IN': (d) => `Naya AT deposit request aaya. ₹${d.amount || 0}.`,
    'en-IN': (d) => `New AT deposit request received. Amount ₹${d.amount || 0}.`,
    'en-US': (d) => `New AT deposit request, ₹${d.amount || 0}.`,
  },
  at_deposit_approved: {
    'hi-IN': (d) => `AT deposit approve ho gaya. ${d.amount || 0} AT wallet mein credit ho gaye.`,
    'en-IN': (d) => `AT deposit approved: ${d.amount || 0} AT credited to your wallet.`,
    'en-US': (d) => `AT deposit approved, ${d.amount || 0} AT credited.`,
  },
  at_deposit_rejected: {
    'hi-IN': (d) => `AT deposit reject ho gaya. ₹${d.amount || 0} refund pending — admin note check karein.`,
    'en-IN': (d) => `AT deposit rejected: ₹${d.amount || 0}. Please check the admin note.`,
    'en-US': (d) => `AT deposit rejected: ₹${d.amount || 0}.`,
  },
  // Wave 50 · Group Buy
  group_buy_join: {
    'hi-IN': (d) => `Group buy me nayi joining. ${d.joined || 0} me se ${d.min_buyers || 0} buyers.`,
    'en-IN': (d) => `New group buy join · ${d.joined || 0} of ${d.min_buyers || 0} buyers.`,
    'en-US': (d) => `Group buy joined, ${d.joined || 0} of ${d.min_buyers || 0} buyers.`,
  },
  group_buy_confirmed: {
    'hi-IN': (d) => `Group buy confirm ho gaya. ${d.orders || 0} orders${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
    'en-IN': (d) => `Group buy confirmed · ${d.orders || 0} orders${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
    'en-US': (d) => `Group buy confirmed, ${d.orders || 0} orders${d.amount ? ', total ' + d.amount + ' AT' : ''}.`,
  },
  group_buy_expired: {
    'hi-IN': () => `Group buy expire ho gaya. Sabko refund ho gaya.`,
    'en-IN': () => `Group buy expired. All buyers refunded.`,
    'en-US': () => `Group buy expired, all refunded.`,
  },
  // Wave 56 · Full system sound box
  payment_sent: {
    'hi-IN': (d) => `${d.amount || 0} AT bheje. ${d.desc ? d.desc + '.' : ''}`,
    'en-IN': (d) => `${d.amount || 0} AT sent. ${d.desc || ''}`,
    'en-US': (d) => `${d.amount || 0} AT sent.`,
  },
  new_registration: {
    'hi-IN': (d) => `Naya member aaya! ${d.name || 'User'} ne platform join kiya.`,
    'en-IN': (d) => `New member joined! ${d.name || 'User'} has registered.`,
    'en-US': (d) => `New member: ${d.name || 'User'} registered.`,
  },
  withdraw_request: {
    'hi-IN': (d) => `Naya withdraw request. ${d.amount || 0} AT nikalne ki request aayi.`,
    'en-IN': (d) => `New withdrawal request: ${d.amount || 0} AT.`,
    'en-US': (d) => `Withdrawal request: ${d.amount || 0} AT.`,
  },
  withdraw_paid: {
    'hi-IN': (d) => `Withdraw approve ho gaya. ${d.amount || 0} AT ka payment bhej diya.`,
    'en-IN': (d) => `Withdrawal paid: ${d.amount || 0} AT transferred.`,
    'en-US': (d) => `Withdrawal paid, ${d.amount || 0} AT.`,
  },
  withdraw_rejected: {
    'hi-IN': (d) => `Withdraw reject ho gaya. ${d.amount || 0} AT wapas wallet mein aa gaye.`,
    'en-IN': (d) => `Withdrawal rejected: ${d.amount || 0} AT refunded to wallet.`,
    'en-US': (d) => `Withdrawal rejected, ${d.amount || 0} AT refunded.`,
  },
  plan_purchase: {
    'hi-IN': (d) => `${d.name || 'Plan'} activate ho gaya. ${d.amount ? d.amount + ' AT kharcha.' : ''}`,
    'en-IN': (d) => `${d.name || 'Plan'} activated. ${d.amount ? d.amount + ' AT charged.' : ''}`,
    'en-US': (d) => `${d.name || 'Plan'} activated.`,
  },
  new_auction_bid: {
    'hi-IN': (d) => `Nayi bid aayi! ${d.title || 'Auction'} par ${d.amount || 0} AT ki bid.`,
    'en-IN': (d) => `New auction bid: ${d.amount || 0} AT on ${d.title || 'your auction'}.`,
    'en-US': (d) => `Auction bid: ${d.amount || 0} AT.`,
  },
};

function pickTemplate(eventKey, lang) {
  const set = TEMPLATES[eventKey] || {};
  return set[lang] || set['en-IN'] || set['hi-IN'] || (() => `Event: ${eventKey}`);
}

// ============ Main announce entrypoint ============
export function announce(eventKey, data = {}) {
  const p = getPrefs();
  if (p.mute) return;
  if (isCategoryMuted(eventKey)) return; // per-category mute check
  const fn = pickTemplate(eventKey, p.lang);
  const text = fn(data);
  playChime();
  setTimeout(() => speak(text), 220);
}

// Fire-and-forget: preload voices (Chrome hydrates asynchronously)
try {
  if (window.speechSynthesis) {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => {
      window.dispatchEvent(new CustomEvent('soundbox:voices-loaded'));
    };
  }
} catch (_) {}
